import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { X } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { TutorMascot } from "./tutor-mascot";

const HINT_KEY = "examora.fridayHint";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function hintShownToday() {
  try {
    return localStorage.getItem(HINT_KEY) === today();
  } catch {
    return true;
  }
}

function rememberHint() {
  try {
    localStorage.setItem(HINT_KEY, today());
  } catch {
    // storage blocked — the hint simply may show again
  }
}

/**
 * Friday, the AI tutor, as a round button in the bottom-right corner of every student page (above the
 * phone tab bar). Once a day, on the dashboard, a small speech bubble invites the student to chat; closing it — or opening
 * Friday — hides it until tomorrow. Not shown on Friday's own page.
 */
export function TutorLauncher() {
  const t = useTranslations("exams.tutor.launcher");
  const pathname = usePathname();
  const [hint, setHint] = useState(false);
  const onTutorPage = pathname === "/tutor";

  useEffect(() => {
    // The bubble only on the dashboard: on list pages it would cover the content.
    if (pathname !== "/dashboard" || hintShownToday()) return;
    const show = window.setTimeout(() => setHint(true), 4000);
    const hide = window.setTimeout(() => {
      setHint(false);
      rememberHint();
    }, 16000);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [pathname]);

  if (onTutorPage) return null;

  return (
    // Hidden while a vocabulary session runs (body[data-focus-session], set by ReviewSession): on phones it
    // would cover the session's "Davom etish" button.
    <div className="tutor-launcher fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] right-4 z-40 flex items-end gap-2 lg:bottom-6 lg:right-6">
      {hint && pathname === "/dashboard" && (
        <div className="animate-fade-in-up relative mb-3 max-w-[13rem] rounded-2xl rounded-br-sm border border-border bg-card px-3.5 py-2.5 text-sm shadow-lg">
          <button
            type="button"
            aria-label={t("close")}
            onClick={() => {
              setHint(false);
              rememberHint();
            }}
            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <p className="font-semibold">{t("hintTitle")}</p>
          <p className="text-muted-foreground">{t("hintBody")}</p>
        </div>
      )}
      <Link
        href="/tutor"
        onClick={rememberHint}
        aria-label={t("open")}
        className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-background bg-primary/25 shadow-xl transition-transform hover:scale-105 active:scale-95 lg:h-[4.5rem] lg:w-[4.5rem]"
      >
        <TutorMascot size={52} />
      </Link>
    </div>
  );
}
